import { httpClient } from '../utils/httpClient';
import Logger from '../utils/logger';
import get from 'lodash/get';
import { IMAGE_PROMPT_GENERATE, IMAGE_GENERATE } from '../constants/apiRoutes';

// API Response Types
interface PromptGenerationResponse {
  success: boolean;
  message: string;
  data?: {
    prompt?: string;
  };
}

interface ImageGenerationResponse {
  success: boolean;
  message: string;
  statusCode?: number;
  status?: string;
  timestamp?: string;
  errors?: string[];
  data?: {
    fireflyResponse?: string;
    imageJobId?: string;
  };
}

// API Payload Types
interface PromptGenerationPayload {
  story: string;
  subject: string;
  style: string;
  purpose: string;
}

interface ImageGenerationPayload {
  prompt: string;
}

/**
 * Generates a prompt based on user inputs
 */
export const generatePrompt = async (
  payload: PromptGenerationPayload
): Promise<PromptGenerationResponse> => {
  try {
    console.log(
      '🚀 PROMPT GENERATION REQUEST:',
      JSON.stringify(payload, null, 2)
    );

    const response = await httpClient.post<PromptGenerationResponse>(
      IMAGE_PROMPT_GENERATE,
      payload
    );

    // Enhanced debugging for response structure
    console.log('🔍 FULL RESPONSE OBJECT:', JSON.stringify(response, null, 2));
    console.log('🔍 RESPONSE TYPE:', typeof response);
    console.log(
      '🔍 RESPONSE KEYS:',
      response && typeof response === 'object'
        ? Object.keys(response)
        : 'Not an object'
    );

    // Log individual response properties
    if ((response as any).data) {
      console.log(
        '🔍 response.data:',
        JSON.stringify((response as any).data, null, 2)
      );
    }
    if ((response as any).body) {
      console.log(
        '🔍 response.body:',
        JSON.stringify((response as any).body, null, 2)
      );
    }

    // Enhanced response structure handling with priority order
    let extractedData = null;
    let dataSource = '';

    // Priority 1: Check if response has success and data properties directly (MSW format)
    if (
      response &&
      (response as any).success !== undefined &&
      (response as any).data
    ) {
      extractedData = (response as any).data;
      dataSource = 'direct response (MSW format)';
      console.log(
        '✅ Found data in direct response (MSW format):',
        extractedData
      );
    }
    // Priority 2: Check response.data with success property
    else if (
      (response as any).data &&
      (response as any).data.success !== undefined
    ) {
      extractedData = (response as any).data.data || (response as any).data;
      dataSource = 'response.data with success';
      console.log(
        '✅ Found data in response.data with success:',
        extractedData
      );
    }
    // Priority 3: Check response.body (Axios format)
    else if ((response as any).body && (response as any).body.data) {
      extractedData = (response as any).body.data;
      dataSource = 'response.body.data';
      console.log('✅ Found data in response.body.data:', extractedData);
    }
    // Priority 4: Check response.data.data (nested format)
    else if ((response as any).data && (response as any).data.data) {
      extractedData = (response as any).data.data;
      dataSource = 'response.data.data';
      console.log('✅ Found data in response.data.data:', extractedData);
    }
    // Priority 5: Check response.data (simple format)
    else if ((response as any).data) {
      extractedData = (response as any).data;
      dataSource = 'response.data';
      console.log('✅ Found data in response.data:', extractedData);
    }
    // Priority 6: Check response.body (alternative format)
    else if ((response as any).body) {
      extractedData = (response as any).body;
      dataSource = 'response.body';
      console.log('✅ Found data in response.body:', extractedData);
    }
    // Priority 7: Use direct response as fallback
    else {
      extractedData = response;
      dataSource = 'direct response';
      console.log('✅ Using direct response:', extractedData);
    }

    console.log(`📍 DATA SOURCE: ${dataSource}`);
    console.log('🔍 EXTRACTED DATA:', JSON.stringify(extractedData, null, 2));

    // Check for prompt in the extracted data
    if (extractedData && extractedData.prompt) {
      console.log('✅ SUCCESS - PROMPT FOUND:', extractedData.prompt);
      console.log('✅ PROMPT LENGTH:', extractedData.prompt.length);
      return {
        success: true,
        data: extractedData,
        message: 'Prompt generated successfully',
      };
    }

    // Additional fallback: check if response itself has prompt property
    if ((response as any).prompt) {
      console.log(
        '✅ SUCCESS - PROMPT FOUND IN DIRECT RESPONSE:',
        (response as any).prompt
      );
      return {
        success: true,
        data: { prompt: (response as any).prompt },
        message: 'Prompt generated successfully',
      };
    }

    console.log('❌ NO PROMPT FOUND IN ANY STRUCTURE');
    console.log(
      '❌ AVAILABLE PROPERTIES IN EXTRACTED DATA:',
      extractedData ? Object.keys(extractedData) : 'No extracted data'
    );

    return {
      success: false,
      message: 'Failed to generate prompt - no prompt property found',
    };
  } catch (error) {
    console.error('❌ PROMPT GENERATION ERROR:', error);
    Logger.error(
      new Error(
        get(
          error,
          'response.body.message',
          get(error, 'response.data.message', 'Error generating prompt')
        )
      )
    );

    return {
      success: false,
      message:
        get(
          error,
          'response.body.message',
          get(error, 'response.data.message', 'Error generating prompt')
        ) || 'An error occurred',
    };
  }
};

/**
 * Generates an image from a prompt
 */
export const generateImage = async (
  payload: ImageGenerationPayload
): Promise<ImageGenerationResponse> => {
  try {
    const response = await httpClient.post<ImageGenerationResponse>(
      IMAGE_GENERATE,
      payload
    );

    // Handle rate limit response (200 OK but with success: false)
    // Check response.body, response.data, and direct response for rate limit info
    const responseData =
      (response as any).body || (response as any).data || response;
    if (responseData && (responseData as any).success === false) {
      return {
        success: false,
        message: (responseData as any).message || 'Request failed',
        statusCode: (responseData as any).statusCode,
        status: (responseData as any).status,
        timestamp: (responseData as any).timestamp,
        errors: (responseData as any).errors,
      };
    }

    const { data } = (
      (response as any).body
        ? { data: (response as any).body }
        : (response as any).data
          ? response
          : { data: response }
    ) as ImageGenerationResponse;

    if (data) {
      return {
        success: true,
        data,
        message: 'Image generated successfully',
      };
    }

    return {
      success: false,
      message: 'Failed to generate image',
    };
  } catch (error) {
    Logger.error(
      new Error(get(error, 'response.data.message', 'Error generating image'))
    );

    const errorResponse = get(error, 'response.data', {});

    return {
      success: false,
      message:
        get(error, 'response.data.message', 'Error generating image') ||
        'An error occurred',
      statusCode:
        get(error, 'response.status') || get(errorResponse, 'statusCode'),
      status: get(errorResponse, 'status'),
      timestamp: get(errorResponse, 'timestamp'),
      errors: get(errorResponse, 'errors'),
    };
  }
};
